<template>
  <section class="chat-view">
    <section class="conversation-title">
      <div v-if="projectIsOnline && !canToggleStatus" class="status-container">
        <i class="online-circle pr-2 fas fa-circle"></i>
        <p class="online-status">ONLINE</p>
      </div>
      <div v-if="!projectIsOnline && !canToggleStatus" class="status-container">
        <i class="fal fa-circle pr-2"></i>
        <p class="online-status">OFFLINE</p>
      </div>
      <div v-if="canToggleStatus" class="online-toggle">
        <toggle-switch v-model="appearOnline"></toggle-switch>
        <label v-if="projectIsOnline">ONLINE</label>
        <label v-if="!projectIsOnline">OFFLINE</label>
      </div>
      <h1 class="title has-text-centered is-size-4">{{ $t('projectPage.liveChatTitle') }}</h1>
      <h2 class="subtitle is-size-6 pt-2 has-text-centered">{{ $t('projectPage.liveChatSubtitle') }}</h2>
    </section>
    <section v-if="!hasConversation" class="start-container is-flex">
      <button class="start-button button is-primary is-uppercase has-text-weight-bold" @click="createProjectConversation">
        <i class="message-icon fal fa-comments-alt"></i>
        {{ $t('projectPage.startLiveConversation') }}
      </button>
    </section>
    <section v-if="hasConversation" class="conversation-area">
      <messages v-if="conversationMessages.length > 0" ref="messages" class="message-area"
                :conversation="conversation" :users="conversationUsers" :chatType="chatType" v-on:showModal="seenDeleteModal = true, idToDelete = $event">
      </messages>
      <add-message ref="addMessage" :chatType="chatType" :conversation="conversation" @scrollToBottom="scrollToBottom()"></add-message>
    </section>
    <modal-custom v-if="seenDeleteModal" :title="'Delete Message'"
                  :text="'Are you sure you want to delete this message?'"
                  :hasFooter="true" :successText="'Delete'" :cancelText="'Cancel'"
                  @modalSuccess="deleteMessage(idToDelete, checkLiveChatUserRole)" @modalCancel="seenDeleteModal = false">
    </modal-custom>
    <mute-toggle :conversation="conversation"> </mute-toggle>
  </section>
</template>

<script>
import state from '../state'
import MuteToggle from '@/components/Pages/Messaging/Conversations/UIMuteToggle'
import Messages from '@/components/Pages/Messaging/Messages'
import ModalCustom from '@/components/Modals/Custom'
import ToggleSwitch from '@/components/Controls/ToggleSwitch'

import AddMessage from '@/components/Inputs/AddMessage'
import addMessageMixin from '@/components/Inputs/AddMessage/addMessageMixin'

import auth from '@/mixins/auth'
import componentState from '@/mixins/component-state'
import debounce from 'lodash/debounce'

export default {
  props: ['stateKeyValue'],
  components: {
    Messages,
    AddMessage,
    ModalCustom,
    MuteToggle,
    ToggleSwitch
  },
  mixins: [auth, componentState, addMessageMixin],
  data: function () {
    return {
      conversation: {},
      chatType: 'LiveChat',
      appearOnline: null
    }
  },
  mounted () {
    this.conversation.notifications = true
    this.appearOnline = this.project.isOnline
  },
  watch: {
    stateKeyValue: function (value) {
      this._setObjectKey(value)
    },
    project: function () {
      this.getProjectConversation()
    },
    appearOnline: function () {
      this.updateIsOnline(this.appearOnline)
    }
  },
  computed: {
    hasConversation: function () {
      return this.conversation !== null
    },
    project: function () {
      return this._get(state.project.id) ? this.$store.getters.project(this._get(state.project.id)) || {} : {}
    },
    conversationUsers: function () {
      return this.conversation?.users ? this.conversation.users : []
    },
    conversationMessages: function () {
      return this.conversation?.messages ? this.conversation.messages : []
    },
    showPermission: function () {
      return this._get(state.project.permissions.liveChat)
    },
    userLiveChatRole: function () {
      return this._get(state.project.role)
    },
    checkLiveChatUserRole: function () {
      return this.chatType === 'LiveChat' && (this.showPermission || (this.userLiveChatRole === 'co-owner' || this.userLiveChatRole === 'owner' || this.userLiveChatRole === 'anonAdmin' || this.userLiveChatRole === 'admin'))
    },
    projectIsOnline () {
      return this.appearOnline
    },
    canToggleStatus: function () {
      return this.userLiveChatRole === 'owner' || this.userLiveChatRole === 'co-owner' || this.isAdmin
    }
  },
  methods: {
    getProjectConversation: function () {
      if (!this.project) {
        return
      }
      this.$store.dispatch('getChatConversation', { projectID: this.project.id, chatType: this.chatType }).then((projectConversation) => {
        this.conversation = projectConversation
      })
    },
    createProjectConversation: function () {
      this.$store.dispatch('createChatConversation', { projectID: this.project.id, chatType: this.chatType }).then((projectConversation) => {
        this.conversation = projectConversation
      })
    },
    updateIsOnline: debounce(function (appearOnline) {
      const params = { id: this.project.id, isOnline: appearOnline }
      this.$store.dispatch('updateProjectIsOnline', params).then((value) => {
        this._set(state.project.isOnline, value.isOnline)
      })
    }, 500, { leading: true, trailing: false })
  }
}

</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';
.online-toggle {
  position: absolute;
  top: 18%;
  left: 2%;
  display: flex;
  align-items: center;
  @include mobile {
    flex-direction: column;
    top: 13%;
  }
  label {
    font-size: 0.8em;
    padding-left: 10px;
    @include mobile {
      font-size: 0.5em;
      padding-left: 6px;
    }
  }
}
.online-circle {
  color: #06b115;
}
.status-container {
  font-size: 0.65rem;
  display: flex;
  align-items: center;
  position: absolute;
  left: 20px;
  @include mobile {
    left: 10px;
    top: 10px;
  }
}
.chat-view {
  overflow: hidden;
  width: 100%;
  .conversation-title {
    border-bottom: 1px solid whitesmoke;
    padding: 1rem 0;
    position: relative;
  }
  .start-container {
    width: 100%;
    justify-content: space-around;
    .start-button {
      margin: 2rem auto;
      font-size: 0.65rem;
      border-radius: 0;
      .message-icon {
        padding-top: 0.1rem;
        font-size: 0.9rem;
        margin-right: 0.5rem;
      }
    }
  }
}
.conversation-area {
  height: 100%;
  background-color: #f2f2f2;
  .message-area {
    max-height: 450px;
  }
}
</style>
